import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import GavelIcon from '@mui/icons-material/Gavel';
import BalanceIcon from '@mui/icons-material/Balance';
import {
  CARD_BORDER_RADIUS,
  colors,
  RIGHT_RAIL_GAP,
  RIGHT_RAIL_WIDTH,
} from './constants.js';
import MunicipalityNotesInput from './MunicipalityNotesInput.js';
import MunicipalityContacts from './MunicipalityContacts.js';
import MuniIndicatorBadge from './MuniIndicatorBadge.js';

const MUNI_DETAIL_TAB = {
  MUNI_INFO: 'muniInfo',
  CONTACTS: 'contacts',
};

function DetailTab({ active, onClick, children }) {
  const tabRadius = CARD_BORDER_RADIUS * 4;
  return (
    <Button
      size="small"
      disableRipple
      onClick={onClick}
      sx={{
        minWidth: 0,
        ml: 0,
        px: 1,
        py: 0.35,
        fontSize: '0.72rem',
        fontWeight: active ? 600 : 500,
        lineHeight: 1.2,
        textTransform: 'none',
        color: active ? colors.accent : colors.textSecondary,
        bgcolor: active ? colors.bgPrimary : 'transparent',
        border: 'none',
        ...(active && {
          borderTop: '1px solid',
          borderRight: '1px solid',
          borderLeft: '1px solid',
          borderBottom: `1px solid ${colors.bgPrimary}`,
          borderColor: colors.borderTertiary,
        }),
        borderRadius: `${tabRadius}px ${tabRadius}px 0 0`,
        mb: active ? '-1px' : 0,
        zIndex: active ? 1 : 0,
        position: 'relative',
        boxSizing: 'border-box',
        '&:hover': { bgcolor: active ? colors.bgPrimary : 'rgba(0,0,0,0.04)' },
      }}
    >
      {children}
    </Button>
  );
}

function MunicipalityDetailPanel({
  municipalityNotes,
  onNotesChange,
  isSavingNotes,
  contacts,
  onSaveTownContacts,
  loadingNotes = false,
  loadingContacts = false,
  loadingIndicators = false,
  masterPetition,
  barReviewActive,
  barReviewTooltip,
}) {
  const [activeTab, setActiveTab] = useState(MUNI_DETAIL_TAB.MUNI_INFO);
  const [isEditingContacts, setIsEditingContacts] = useState(false);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsEditingContacts(false);
  };

  const notesTabActive = activeTab === MUNI_DETAIL_TAB.MUNI_INFO;

  return (
    <Box sx={{ mt: 0.375 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1fr) ${RIGHT_RAIL_WIDTH}px`,
          gridTemplateRows: 'auto auto',
          columnGap: 1,
          rowGap: 0,
        }}
      >
        <Box
          sx={{
            gridColumn: 1,
            gridRow: 1,
            display: 'flex',
            gap: 0.25,
            minWidth: 0,
          }}
        >
          <DetailTab
            active={notesTabActive}
            onClick={() => handleTabChange(MUNI_DETAIL_TAB.MUNI_INFO)}
          >
            Muni Notes
          </DetailTab>
          <DetailTab
            active={activeTab === MUNI_DETAIL_TAB.CONTACTS}
            onClick={() => handleTabChange(MUNI_DETAIL_TAB.CONTACTS)}
          >
            Contacts
          </DetailTab>
        </Box>

        <Box
          sx={{
            gridColumn: 1,
            gridRow: 2,
            minWidth: 0,
            overflow: 'visible',
          }}
        >
          {notesTabActive ? (
            <MunicipalityNotesInput
              value={municipalityNotes}
              onChange={onNotesChange}
              isSaving={isSavingNotes}
              loading={loadingNotes}
            />
          ) : (
            <MunicipalityContacts
              contacts={contacts}
              isEditing={isEditingContacts}
              onEditingChange={setIsEditingContacts}
              onSaveTownContacts={onSaveTownContacts}
              loading={loadingContacts}
            />
          )}
        </Box>

        <Box
          sx={{
            gridColumn: 2,
            gridRow: 2,
            display: 'flex',
            gap: RIGHT_RAIL_GAP,
            alignSelf: 'start',
            pb: 0.25,
          }}
        >
          <MuniIndicatorBadge
            icon={<GavelIcon fontSize="inherit" />}
            label="Master Petition"
            active={masterPetition}
            loading={loadingIndicators}
            tooltip={masterPetition ? 'Master Petition municipality' : 'Not a Master Petition municipality'}
          />
          <MuniIndicatorBadge
            icon={<BalanceIcon fontSize="inherit" />}
            label="BAR Review"
            active={barReviewActive}
            loading={loadingIndicators}
            tooltip={barReviewTooltip}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(MunicipalityDetailPanel);
